// Module 19 (BL-380) G2 - the guest list's reads and writes. Every call runs under the signed-in user's own RLS.
// Nothing here writes to contacts or sends a message.

import { loadEventStatuses } from '../eventsApi';

const BASE_GUEST_COLUMNS = 'id, engagement_id, first_name, last_name, email, phone, group_id, plus_one_of, plus_ones_allowed, rsvp_status, notes, created_at, updated_at';
// G6 (migration 241): the shuttle flag and arrival details.
const GUEST_COLUMNS = `${BASE_GUEST_COLUMNS}, needs_shuttle, arrival_info`;
const SHUTTLE_KEYS = ['needs_shuttle', 'arrival_info'];

// True when the database does not have the G6 columns yet (code live minutes before migration 241 is applied).
export function missingShuttleColumns(error) {
    const text = `${error?.code || ''} ${error?.message || ''}`;
    return /42703|PGRST204/.test(text) && /needs_shuttle|arrival_info/.test(text);
}
const withoutShuttle = (row) => Object.fromEntries(Object.entries(row).filter(([key]) => !SHUTTLE_KEYS.includes(key)));

// Runs `write(row, columns)`; if the G6 columns are not there yet, runs it once more without them (and says so only if a guest was flagged).
async function writeGuest(write, row) {
    const first = await write(row, GUEST_COLUMNS);
    if (!first.error) return first.data;
    if (!missingShuttleColumns(first.error)) throw first.error;
    if (row.needs_shuttle === true) throw new Error('The shuttle cannot be saved yet. Please try again in a few minutes.');
    const second = await write(withoutShuttle(row), BASE_GUEST_COLUMNS);
    if (second.error) throw second.error;
    return second.data;
}

function blank(value) {
    const text = String(value ?? '').trim();
    return text === '' ? null : text;
}

function guestRow(values) {
    const plus = Number(values.plus_ones_allowed);
    return {
        first_name: String(values.first_name || '').trim(),
        last_name: blank(values.last_name),
        email: blank(values.email) ? String(values.email).trim().toLowerCase() : null,
        phone: blank(values.phone),
        group_id: values.group_id || null,
        rsvp_status: values.rsvp_status || 'pending',
        plus_ones_allowed: Number.isFinite(plus) ? plus : 0,
        notes: blank(values.notes),
        // G6: only sent when the form asked (the shuttle question is on)
        ...('needs_shuttle' in values ? { needs_shuttle: values.needs_shuttle === true, arrival_info: values.needs_shuttle === true ? blank(values.arrival_info) : null } : {}),
    };
}

export async function loadEventForGuests(client, engagementId) {
    const read = (columns) => client.from('event_engagement_details').select(columns).eq('engagement_id', engagementId).maybeSingle();
    const base = 'engagement_id, event_name, event_date, start_time, end_time, venue_name, expected_guest_count';
    let { data, error } = await read(`${base}, end_date, plus_ones_enabled, plus_ones_max`);
    // Code live minutes before migration 289 is applied: read the columns that exist.
    if (error && /42703|end_date|plus_ones/.test(`${error.code || ''} ${error.message || ''}`)) ({ data, error } = await read(base));
    if (error) throw error;
    if (!data) return null;
    // the status lives on engagements (not readable by an Events-only planner), so it comes through the checked function
    const statuses = await loadEventStatuses(client, engagementId);
    return { ...data, engagement: statuses.get(engagementId) || null };
}

export async function loadGuests(client, engagementId) {
    const read = (columns) => client.from('event_guests').select(columns).eq('engagement_id', engagementId).order('created_at', { ascending: true });
    let { data, error } = await read(GUEST_COLUMNS);
    if (error && missingShuttleColumns(error)) ({ data, error } = await read(BASE_GUEST_COLUMNS));
    if (error) throw error;
    return data || [];
}

export async function loadGroups(client, engagementId) {
    const { data, error } = await client
        .from('event_guest_groups')
        .select('id, engagement_id, name, sort_order')
        .eq('engagement_id', engagementId)
        .order('sort_order', { ascending: true })
        .order('name', { ascending: true });
    if (error) throw error;
    return data || [];
}

export async function createGuest(client, engagementId, values) {
    return writeGuest((row, columns) => client.from('event_guests').insert(row).select(columns).single(), { engagement_id: engagementId, ...guestRow(values) });
}

export async function updateGuest(client, guestId, values, userId) {
    return writeGuest((row, columns) => client.from('event_guests').update(row).eq('id', guestId).select(columns).single(), { ...guestRow(values), updated_by: userId || null });
}

export async function setGuestRsvp(client, guestId, status, userId) {
    const { error } = await client.from('event_guests').update({ rsvp_status: status, updated_by: userId || null }).eq('id', guestId);
    if (error) throw error;
}

export async function deleteGuest(client, guestId) {
    const { error } = await client.from('event_guests').delete().eq('id', guestId);
    if (error) throw error;
}

// A plus-one is its own row linked to its host. It sits in the host's group and cannot have plus-ones of its own.
export async function createPlusOne(client, host, values) {
    const row = { ...guestRow({ ...values, plus_ones_allowed: 0 }), group_id: host.group_id || null };
    return writeGuest((next, columns) => client.from('event_guests').insert(next).select(columns).single(), { engagement_id: host.engagement_id, plus_one_of: host.id, ...row });
}

export async function createGroup(client, engagementId, name, sortOrder = 0) {
    const { data, error } = await client.from('event_guest_groups').insert({ engagement_id: engagementId, name: String(name || '').trim(), sort_order: sortOrder }).select('id, engagement_id, name, sort_order').single();
    if (error) throw error;
    return data;
}

export async function renameGroup(client, groupId, name, userId) {
    const { error } = await client.from('event_guest_groups').update({ name: String(name || '').trim(), updated_by: userId || null }).eq('id', groupId);
    if (error) throw error;
}

// Deleting a group un-groups its guests (the database sets their group to none); it never deletes a guest.
export async function deleteGroup(client, groupId) {
    const { error } = await client.from('event_guest_groups').delete().eq('id', groupId);
    if (error) throw error;
}

export async function importGuests(client, engagementId, rows) {
    const { data, error } = await client.rpc('m19_import_guests', { p_engagement_id: engagementId, p_rows: rows });
    if (error) throw error;
    return data;
}
